import {Alert} from 'src/design/atoms/Alert';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useCallback, useMemo, useState} from 'react';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import type {
  ExtensionFormTabSubmissionResponse,
  FormStateEnum} from 'src/api/ProactApiClient';
import {
  FormSubmissionScopeEnum,
  ModuleEnum,
  PermissionOperationEnum
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {getFormPillConfig} from 'src/app/forms/formHelpers';
import {useGetFormTabSubmissionsQuery} from 'src/redux/queries/moduleExtension';
import {useFormSubmissionExport} from 'src/shared/hooks/useFormSubmissionExport';
import {ModuleEntityTypeMap} from './moduleEntityTypeMap';
import type {FormSubmissionDrawerMode} from 'src/app/forms/FormSubmissionDrawer';
import {FormSubmissionDrawer} from 'src/app/forms/FormSubmissionDrawer';

export interface ExtensionFormTabProps {
  formTabId: number;
  formDefinitionId: number;
  targetEntityId: number;
  module: ModuleEnum;
  formDefinitionExists?: boolean;
}

export const ExtensionFormTab: React.FC<ExtensionFormTabProps> = ({
  formTabId,
  formDefinitionId,
  targetEntityId,
  module,
  formDefinitionExists = true,
}) => {
  ExtensionFormTab.displayName = 'ExtensionFormTab';

  const targetEntityType = ModuleEntityTypeMap[module];

  // Drawer state
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerMode, setDrawerMode] = useState<FormSubmissionDrawerMode>('create');
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<
    number | undefined
  >();

  const openCreateDrawer = useCallback(() => {
    setDrawerMode('create');
    setSelectedSubmissionId(undefined);
    setDrawerOpened(true);
  }, []);

  const openViewDrawer = useCallback((submissionId: number) => {
    setDrawerMode('view');
    setSelectedSubmissionId(submissionId);
    setDrawerOpened(true);
  }, []);

  const columns = useMemo<
    MRT_ColumnDef<ExtensionFormTabSubmissionResponse>[]
  >(
    () => [
      {
        accessorKey: 'internalIdentifier',
        header: 'Identifier',
      },
      {
        accessorKey: 'state',
        header: 'Status',
        enableColumnFilter: false,
        enableColumnFilterModes: false,
        Cell: ({cell}) => {
          const cellValue = cell.getValue<FormStateEnum>();
          const pillConfigs = getFormPillConfig(cellValue);
          return (
            <>
              {pillConfigs.map((pillOptions, index) => (
                <Pill
                  key={index}
                  text={pillOptions.text}
                  color={pillOptions.color}
                />
              ))}
            </>
          );
        },
      },
      {
        accessorKey: 'createdByName',
        header: 'Created By',
      },
      {
        accessorKey: 'createdAt',
        header: 'Created Date',
        Cell: ({cell}) => (
          <>{cell.getValue<Date>()?.toString().toShortDate()}</>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: `ext-form-tab-${formTabId}`,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
    defaultSorting: [{id: 'createdAt', desc: true}],
    defaultShowColumnFilters: false,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetFormTabSubmissionsQuery(
      {
        moduleExtensionFormTabId: formTabId,
        targetEntityId: targetEntityId,
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {handleExport, isExporting} = useFormSubmissionExport(
    FormSubmissionScopeEnum.ExtensionTab,
    {
      moduleExtensionFormTabId: formTabId,
      targetEntityId: targetEntityId,
    },
    {filters, filterFns, sorting}
  );

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => openViewDrawer(row.original.id)}
          />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <PermissionGuard
              module={ModuleEnum.Forms}
              operation={PermissionOperationEnum.Export}>
              <TableToolbar.Action
                variant="export"
                onClick={handleExport}
                loading={isExporting}
              />
            </PermissionGuard>
            <TableToolbar.Action variant="create" onClick={openCreateDrawer}>
              New Submission
            </TableToolbar.Action>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  if (!formDefinitionExists) {
    return (
      <Alert color="yellow" title="Form unavailable">
        The form definition associated with this tab is no longer available.
      </Alert>
    );
  }

  if (!targetEntityType) {
    return (
      <Alert color="yellow" title="Unsupported module">
        This module does not support extension form tabs.
      </Alert>
    );
  }

  return (
    <ContentWrapper>
      {table}
      {targetEntityType && (
        <FormSubmissionDrawer
          data-testid="extension-form-drawer"
          opened={drawerOpened}
          onClose={() => setDrawerOpened(false)}
          onSubmissionChange={refetch}
          formDefinitionId={formDefinitionId}
          createOptions={{
            extensionTabId: formTabId,
            targetEntityType,
            targetEntityId,
          }}
          formSubmissionId={selectedSubmissionId}
          initialMode={drawerMode}
        />
      )}
    </ContentWrapper>
  );
};
